import {
  agentPanelAgents,
  type AgentPanelModel,
} from "@amoreg/client-runtime/state/subagentRuntime";
import { leadLiveSubagent } from "@amoreg/client-runtime/state/subagentStatus";
import {
  AlarmClockIcon,
  CheckCircle2Icon,
  CircleDashedIcon,
  EyeIcon,
  RefreshCwIcon,
} from "lucide-react";
import { createContext, type ComponentProps, type ReactNode } from "react";

import { threadSyncLabel } from "../../threadSync";
import { cn } from "~/lib/utils";
import type { ThreadEndItem } from "./ThreadEnd.logic";
import { QueuedMessagesControl, type QueuedMessagesControlProps } from "./QueuedMessagesControl";
import { SubagentLiveDetail } from "./SubagentLiveStatus";

/**
 * Carries the rendered thread end into the timeline's last row. A context,
 * not a prop, so the memoized timeline does not re-render when it changes.
 */
export const ThreadEndSlot = createContext<ReactNode>(null);

export interface ThreadEndBackgroundWork {
  readonly agentPanelModel: AgentPanelModel;
  readonly stopping: boolean;
  readonly onStop: () => void;
  readonly onOpenAgents: () => void;
}

export interface ThreadEndLifecycle {
  readonly waking: boolean;
  readonly unsettling: boolean;
  readonly onWake: () => void;
  readonly onUnsettle: () => void;
  readonly onDismissWoke: () => void;
}

function ThreadEndAction({ className, ...props }: ComponentProps<"button">) {
  return (
    <button
      type="button"
      {...props}
      className={cn(
        "cursor-pointer rounded-sm text-foreground/80 outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default disabled:opacity-50",
        className,
      )}
    />
  );
}

function ThreadEndLine(props: {
  readonly kind: ThreadEndItem["kind"];
  readonly icon: ReactNode;
  readonly title: ReactNode;
  readonly description?: ReactNode;
  readonly actions?: ReactNode;
  readonly role?: "status";
}) {
  return (
    <div
      role={props.role}
      data-thread-end={props.kind}
      className="flex min-w-0 items-center gap-1.5 px-1 pb-2 text-xs text-muted-foreground [&_svg]:size-3.5 [&_svg]:shrink-0"
    >
      <span aria-hidden className="flex size-3.5 shrink-0 items-center justify-center">
        {props.icon}
      </span>
      <span className="min-w-0 truncate">
        {props.title}
        {props.description ? (
          <>
            {" "}
            <span className="ms-0.5 text-muted-foreground/70">{props.description}</span>
          </>
        ) : null}
      </span>
      {props.actions ? (
        <span className="ms-1.5 flex shrink-0 items-center gap-2.5">{props.actions}</span>
      ) : null}
    </div>
  );
}

/**
 * Once the turn settles the composer's stop button is gone, so this line is
 * the only stop control. A bare "1 agent working" over a quiet stretch read
 * as stuck and the user nudged a busy worker (thread b6b72d1e, 2026-10-10),
 * so it names the newest activity and how long it has run.
 */
export function BackgroundWorkLine({
  liveness,
  work,
}: {
  readonly liveness: "working" | "monitoring";
  readonly work: ThreadEndBackgroundWork;
}) {
  const working = liveness === "working";
  const liveCount = work.agentPanelModel.liveCount;
  const leadAgent = working ? leadLiveSubagent(agentPanelAgents(work.agentPanelModel)) : null;
  return (
    <ThreadEndLine
      kind="background-work"
      icon={working ? <CircleDashedIcon /> : <EyeIcon />}
      title={
        working
          ? liveCount > 0
            ? `${liveCount} ${liveCount === 1 ? "agent" : "agents"} working`
            : "Background work"
          : "Monitoring"
      }
      description={leadAgent ? <SubagentLiveDetail agent={leadAgent} /> : undefined}
      actions={
        <>
          {liveCount > 0 ? (
            <ThreadEndAction className="text-muted-foreground" onClick={work.onOpenAgents}>
              Open
            </ThreadEndAction>
          ) : null}
          <ThreadEndAction disabled={work.stopping} onClick={work.onStop}>
            {work.stopping ? "Stopping..." : "Stop"}
          </ThreadEndAction>
        </>
      }
    />
  );
}

export function ThreadEnd({
  items,
  backgroundWork,
  lifecycle,
  queue,
}: {
  readonly items: ReadonlyArray<ThreadEndItem>;
  readonly backgroundWork: ThreadEndBackgroundWork;
  readonly lifecycle: ThreadEndLifecycle;
  readonly queue: QueuedMessagesControlProps;
}) {
  return (
    <div data-thread-end-root="true" className="pt-1">
      {items.map((item) => {
        switch (item.kind) {
          case "background-work":
            return (
              <BackgroundWorkLine key={item.kind} liveness={item.liveness} work={backgroundWork} />
            );
          case "sync":
            return (
              <ThreadEndLine
                key={item.kind}
                kind="sync"
                role="status"
                icon={<RefreshCwIcon />}
                title={threadSyncLabel(item.phase)}
              />
            );
          case "woke":
            return (
              <ThreadEndLine
                key={item.kind}
                kind="woke"
                icon={<AlarmClockIcon />}
                title="Woke from snooze"
                description="Send a message to keep going."
                actions={
                  <ThreadEndAction
                    aria-label="Dismiss Woke notification"
                    onClick={lifecycle.onDismissWoke}
                  >
                    Dismiss
                  </ThreadEndAction>
                }
              />
            );
          case "parked":
            return item.state === "snoozed" ? (
              <ThreadEndLine
                key={item.kind}
                kind="parked"
                icon={<AlarmClockIcon />}
                title="Snoozed"
                description="Sending a message wakes it."
                actions={
                  <ThreadEndAction disabled={lifecycle.waking} onClick={lifecycle.onWake}>
                    {lifecycle.waking ? "Waking..." : "Wake now"}
                  </ThreadEndAction>
                }
              />
            ) : (
              <ThreadEndLine
                key={item.kind}
                kind="parked"
                icon={<CheckCircle2Icon />}
                title="Settled"
                description="Sending a message moves it back to Active."
                actions={
                  <ThreadEndAction disabled={lifecycle.unsettling} onClick={lifecycle.onUnsettle}>
                    {lifecycle.unsettling ? "Un-settling..." : "Un-settle"}
                  </ThreadEndAction>
                }
              />
            );
          case "up-next":
            return <QueuedMessagesControl key={item.kind} {...queue} />;
        }
      })}
    </div>
  );
}
